import { SpsIcon, ClickableTagKind, MultiSelectTagKind } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";

import * as PropTypes from "../prop-types";
import { SpsMultiSelectTagProps } from "../multi-select/SpsMultiSelectTag";

const propsDoc = {
    disabled: "boolean",
    href: "string",
    kind: "ClickableTagKind",
    onAdd: "() => void",
    onRemove: "() => void",
};

const propTypes = {
    ...PropTypes.spsGlobalPropTypes,
    disabled: PropTypes.bool,
    href: PropTypes.string,
    kind: PropTypes.enumValue<ClickableTagKind>(ClickableTagKind),
    onAdd: PropTypes.fun<() => void>(),
    onRemove: PropTypes.fun<() => void>(),
};

export type SpsClickableTagProps = PropTypes.InferTS<typeof propTypes, HTMLSpanElement>;

export function SpsClickableTag(props: SpsClickableTagProps) {
    const {
        children,
        className,
        disabled,
        href,
        kind = ClickableTagKind.BLUE,
        onAdd,
        onRemove,
        "data-testid": testId,
        unsafelyReplaceClassName,
        ...rest
    } = props;

    const classes = clsx(
        unsafelyReplaceClassName || "sps-clickable-tag",
        `sps-clickable-tag--${kind}`,
        className,
        disabled && "sps-clickable-tag--disabled",
        (onAdd || onRemove) && "sps-clickable-tag--icon"
    );

    const labelClasses = clsx("sps-clickable-tag__label", href && "sps-clickable-tag__label--link");

    const title = React.Children.toArray(children)
        .filter((c) => typeof c === "string")
        .join("");

    function emitEvent(event) {
        if (onAdd) {
            onAdd();
        } else if (onRemove) {
            onRemove();
        }
        event.stopPropagation();
    }

    React.useEffect(() => {
        if (onRemove && onAdd) {
            throw new Error("Clickable tag should have onRemove or onAdd, not both");
        }
    }, [onAdd, onRemove]);

    return (
        <div className={classes} data-testid={testId} {...rest}>
            <span
                className={labelClasses}
                title={title}
                onClick={(event) => event.stopPropagation()}
            >
                {href && <a href={href}>{children}</a>}
                {!href && children}
            </span>
            {(onAdd || onRemove) && !disabled && (
                <>
                    <div
                        className="sps-clickable-tag__icon"
                        onClick={
                            disabled
                                ? () => {}
                                : (event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                                      emitEvent(event)
                        }
                        aria-label={onAdd ? "Add" : "Remove"}
                    >
                        <i
                            aria-hidden="true"
                            className={`sps-icon sps-icon-${onAdd ? SpsIcon.PLUS_SIGN : SpsIcon.X}`}
                            data-testid={`${testId}__icon`}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export function SpsMultiSelectTag(props: SpsMultiSelectTagProps) {
    const { active, kind, children, ...clickableTagProps } = props;

    if (kind === MultiSelectTagKind.RED) {
        console.warn(
            "The red SpsMultiSelectTag style has been removed from SpsClickableTag. This tag will display as blue"
        );
        (clickableTagProps as SpsClickableTagProps).kind = ClickableTagKind.BLUE;
    } else if (kind === MultiSelectTagKind.GRAY) {
        (clickableTagProps as SpsClickableTagProps).kind = ClickableTagKind.GRAY;
    } else if (kind === MultiSelectTagKind.BLUE) {
        (clickableTagProps as SpsClickableTagProps).kind = ClickableTagKind.BLUE;
    }
    console.warn(
        [
            "The SpsMultiSelectTag component has been renamed to SpsClickableTag.",
            "All references to it should be replaced before SpsMultiSelect is removed.",
            "The type of the kind prop is changing from MultiSelectTagKind to ClickableTagKind.",
        ].join(" ")
    );

    return <SpsClickableTag {...clickableTagProps}>{children}</SpsClickableTag>;
}

Object.assign(SpsClickableTag, {
    props: propsDoc,
    propTypes,
    displayName: "SpsClickableTag",
});
